/*
 * MMG-4402: the Deficiency Checklist suggests from the Standard Text Library - the same
 * deficiency codes the Notes modal offers - so admin edits there flow through with no second list.
 */

import { IcomplianceCheckList, IdeficiencyCode } from '../../models';
import { jobTypesIdEnum } from '../../models-enums';

export interface IdeficiencySuggestion {
    value: string;
    label: string;
    standardName: string;
    detail: string;
    searchText: string;
}

export const stripHtml = (html: string): string =>
    (html || '')
        .replace(/<[^>]+>/g, ' ')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/\s+/g, ' ')
        .trim();

export const buildDeficiencySuggestions = (
    codesByID: { [id: string]: IdeficiencyCode },
    standardName: string
): IdeficiencySuggestion[] =>
    Object.values(codesByID || {})
        .filter(code => code && code.isDeleted !== true && !!code.name)
        .map(code => {
            const detail = stripHtml(code.description);

            return {
                value: code.id,
                label: code.name,
                standardName,
                detail,
                searchText: `${code.name} ${standardName} ${detail}`.toLowerCase()
            };
        })
        .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));

export const matchesDeficiencySearch = (suggestion: IdeficiencySuggestion, rawInput: string): boolean => {
    const input = (rawInput || '').trim().toLowerCase();

    // the "create" entry react-select appends carries no searchText and must never be filtered out
    if (!input || !suggestion || !suggestion.searchText) {
        return true;
    }

    return input.split(/\s+/).every(term => suggestion.searchText.indexOf(term) !== -1);
};

/** Only the main name is stored, whether it came from a suggestion or was typed by hand. */
export const resolveDeficiencyName = (typedInput: string): string => (typedInput || '').trim();

/*
 * MMG-4458: a deficiency the engineer typed in on THIS job is theirs to correct or remove. Anything
 * that arrived from somewhere else is not, and there are two ways it can arrive: from a failed
 * measurement point (measurementPointID), or carried forward unresolved from an earlier repair job
 * (originCheckListID). Either one locks the row, including a row this same engineer typed on the
 * previous job - on the new job it is history, not theirs to reword.
 * The server enforces this too - hiding the controls here is convenience, not the control.
 */
export const isManualDeficiency = (
    item: Pick<IcomplianceCheckList, 'measurementPointID' | 'originCheckListID'>
): boolean => !item.measurementPointID && !item.originCheckListID;

/*
 * Falsy checks, not `!== null`: a row cached before these fields existed has them absent, and absent
 * must read as "the engineer typed this", not as locked.
 */
export const canEditDeficiency = (args: { isManual: boolean; isHistoricalMode: boolean }): boolean =>
    args.isManual && !args.isHistoricalMode;

/*
 * MMG-4460: deficiency work belongs to a Repair job. Service Plan carried the whole feature until
 * 2026-09-22 and should not have.
 *
 * Exported and used everywhere rather than written out inline: the same rule previously lived as
 * three separate copies - the render, the download and the Save & Log gate - and a change that
 * updated only two of them left engineers able to see no checklist while the gate still demanded
 * they clear it. Note the name: `isRepairTypeJob` already means two different things in this
 * codebase, so this deliberately does not reuse it.
 */
export const isDeficiencyChecklistJob = (jobTypeID: string | undefined): boolean =>
    jobTypeID === jobTypesIdEnum.repair;
